/**
 * Utilitários para Verificação de Secure Context
 * 
 * Document Picture-in-Picture API requer contexto seguro (HTTPS ou localhost)
 * Este arquivo fornece funções para detectar e lidar com contextos inseguros
 * 
 * @author VISION DESK
 * @version 0.31.9
 */

/**
 * Verifica se está em um contexto seguro (Secure Context)
 * 
 * Contextos Seguros:
 * - https:// (qualquer domínio)
 * - http://localhost
 * - http://127.0.0.1
 * - file:// (localmente)
 * 
 * Contextos Inseguros:
 * - http://192.168.x.x (IPs locais)
 * - http://10.x.x.x (IPs privados)
 * - http://172.16.x.x (IPs privados)
 * - http://qualquer-ip (sem HTTPS)
 */
export const isSecureContext = (): boolean => {
  // 1. Verificação nativa do navegador
  if (typeof window !== 'undefined' && window.isSecureContext !== undefined) {
    return window.isSecureContext;
  }

  // 2. Fallback manual
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;

  // HTTPS é sempre seguro
  if (protocol === 'https:') {
    return true;
  }

  // HTTP só é seguro em localhost ou 127.0.0.1
  if (protocol === 'http:') {
    return (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '[::1]' // IPv6 localhost
    );
  }

  // file:// é considerado seguro
  if (protocol === 'file:') {
    return true;
  }

  // Qualquer outro caso é inseguro
  return false;
};

/**
 * Verifica se está acessando via IP local (rede privada)
 */
export const isLocalNetworkIP = (): boolean => {
  const hostname = window.location.hostname;

  // Regex para IPs privados (192.168.x.x, 10.x.x.x, 172.16-31.x.x)
  const privateIPRegex = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[01])\.)/;

  return privateIPRegex.test(hostname);
};

/**
 * Verifica se PIP nativo pode ser usado
 * 
 * Retorna false se:
 * - Navegador não suporta Document PIP API
 * - Não está em contexto seguro
 * - Está acessando via IP local sem HTTPS
 */
export const canUsePIPNative = (): boolean => {
  // 1. Verificar se navegador suporta
  const hasDocumentPIP = 'documentPictureInPicture' in window;
  if (!hasDocumentPIP) {
    // console.log('⚠️ Secure Context: Navegador não suporta Document PIP API');
    return false;
  }

  // 2. Verificar contexto seguro
  const isSecure = isSecureContext();
  if (!isSecure) {
    // console.log('⚠️ Secure Context: Não está em contexto seguro (requer HTTPS ou localhost)');
    return false;
  }

  return true;
};

/**
 * Obter informações detalhadas sobre o contexto
 */
export const getContextInfo = () => {
  const protocol = window.location.protocol;
  const hostname = window.location.hostname;
  const port = window.location.port;
  const isSecure = isSecureContext();
  const isLocalIP = isLocalNetworkIP();
  const hasDocumentPIP = 'documentPictureInPicture' in window;
  const canUsePIP = canUsePIPNative();

  return {
    protocol,
    hostname,
    port,
    fullURL: window.location.href,
    isSecureContext: isSecure,
    isLocalNetworkIP: isLocalIP,
    hasDocumentPIPAPI: hasDocumentPIP,
    canUsePIPNative: canUsePIP,
    recommendation: getRecommendation(isSecure, isLocalIP, hasDocumentPIP),
  };
};

/**
 * Obter recomendação baseada no contexto
 */
const getRecommendation = (
  isSecure: boolean,
  isLocalIP: boolean,
  hasDocumentPIP: boolean
): string => {
  if (!hasDocumentPIP) {
    return 'Navegador não suporta PIP nativo. Use o fallback modal.';
  }

  if (isSecure) {
    return 'Contexto seguro detectado. PIP nativo disponível.';
  }

  if (isLocalIP) {
    return 'Acesso via IP local (HTTP). Configure HTTPS ou acesse via localhost para usar PIP nativo. Usando fallback modal.';
  }

  return 'Contexto inseguro (HTTP). Configure HTTPS para usar PIP nativo. Usando fallback modal.';
};

/**
 * Exibir informações de diagnóstico no console
 */
export const logContextDiagnostics = () => {
  const info = getContextInfo();

  // console.group('🔐 Secure Context Diagnostics');
  // console.log('URL:', info.fullURL);
  // console.log('Protocol:', info.protocol);
  // console.log('Hostname:', info.hostname);
  // console.log('Port:', info.port);
  // console.log('Is Secure Context:', info.isSecureContext ? '✅' : '❌');
  // console.log('Is Local Network IP:', info.isLocalNetworkIP ? '✅' : '❌');
  // console.log('Has Document PIP API:', info.hasDocumentPIPAPI ? '✅' : '❌');
  // console.log('Can Use PIP Native:', info.canUsePIPNative ? '✅' : '❌');
  // console.log('Recommendation:', info.recommendation);
  // console.groupEnd();

  return info;
};

